/*
 * @Author: Pengzhihui
 * @Description:
 * @Date: 2020-01-19 14:28:27
 * @LastEditTime: 2021-07-22 13:57:51
 */
import "systemjs/dist/system";
import * as singleSpa from "single-spa";
import {
    createSandboxLifecycles,
    mutateLifecycles,
} from "./sandbox/mutateLifecycle";
import EventEmitter from "eventemitter3";
import {
    insertCsss,
    writeConfigToLocalstorage,
    getAppIdsByPath,
} from "./utils/app";
import { EventTypes, DollyConfiguration, LifecyclesIF, MicroAppConfig, MicroAppEntryConfigs, Env, ConfigHandler } from "./interfaces";
import { prefetchResource } from "./utils/prefetch";
import { getStateActions } from "./globalState";
import { getMicroAppConfig } from "./appExplore";

const { registerApplication, navigateToUrl, start: sspaStart, getAppNames, unloadApplication, unregisterApplication, getMountedApps } = (window as any).dolly || singleSpa;

const event = new EventEmitter();

export default class Dolly {
    static async init({
        customProps,
        onJsLoaded,
        env,
        coreId,
        customerConfig,
        prefetch,
        identifyKey,
        configHandler
    }: DollyConfiguration) {
         const dolly = new Dolly({
            customProps,
            onJsLoaded,
            env,
            coreId,
            customerConfig,
            prefetch,
            identifyKey,
            configHandler
        });
        await dolly.getEntryConfig();
        dolly.loadingApps();
        dolly.setPrefetch();
        if (!(window as any).dolly) {
            (window as any).dolly = singleSpa;
        }
        return dolly;
    }
    private appConfigs: MicroAppEntryConfigs = {};
    private customProps: any = {};
    private env: Env = "production";
    private coreId: number = 1;
    private customerConfig = {};
    private prefetch: ((location: any) => boolean) | boolean | undefined = false;
    private configHandler: ConfigHandler = undefined;
    private identifyKey: string | undefined = '';

    constructor({
        customProps,
        onJsLoaded,
        env,
        coreId,
        identifyKey,
        customerConfig,
        prefetch,
        configHandler
    }: DollyConfiguration) {
        if (customProps) {
            this.customProps = customProps;
        }
        if (onJsLoaded) {
            this.onJsLoaded = onJsLoaded;
        }
        if (configHandler) {
            this.configHandler = configHandler;
        }
        this.env = env;
        this.coreId = coreId;
        this.customerConfig = customerConfig;
        this.prefetch = prefetch;
        this.identifyKey = identifyKey;
    }

    // load a Micro app by MicroAppConfig 
    loadOneApp(configItem: MicroAppConfig) {
        const _loadApp = (configItem: MicroAppConfig) => {
            const customProps = this.customProps;
            customProps.navigateToUrl = navigateToUrl;
            customProps.globalEvent = event;
            const globalStateActions = getStateActions(
                `DOLLY-${this.coreId}`,
                false
            );
            const configs = this.appConfigs;
            registerApplication(
                configItem.appName,
                async () => {
                    const appIds = getAppIdsByPath(this.appConfigs);
                    if (configItem.getMutatedLifecycles) {
                        return configItem.getMutatedLifecycles!();
                    }
                    const sbLifecycles = createSandboxLifecycles({
                        beforeBootstrap: () => {
                            insertCsss(configs, appIds);
                        },
                        removeCss: configItem.removeCss,
                        disableJsSandbox: configItem.disableJsSandbox,
                        disableCssSandbox: configItem.disableCssSandbox
                    });
                    const jsExports = await System.import(
                        `${configItem.jsEntry![0]}`
                    );
                    // hooks it when js loaded
                    if (this.onJsLoaded && jsExports.default) {
                        this.onJsLoaded(jsExports.default);
                    }
                    return mutateLifecycles(jsExports.default, sbLifecycles);
                },
                (location: string) => {
                    return new Function("location", configItem.rule)(
                        location
                    );
                },
                {
                    ...customProps,
                    ...globalStateActions,
                    paramsFromConfig: configItem.params || {}
                }
            );
        }
        const appNames = getMountedApps();
        if (!appNames.includes(configItem.appName)) {
            _loadApp(configItem);
        }
    }

    // loading app by app configs
    loadingApps() {
        const configs = this.appConfigs;
        console.info("[DOLLY INFO] MicroApp Configs: ", JSON.stringify(configs));
        Object.keys(configs).forEach((idx: string) => {
            const index = Number(idx);
            this.loadOneApp(configs[index]);        
        });
    }

    // add a MicroApp directly
    // when need add a new router app by component or app entry directly
    addApp({ rule, appId, appName, lifecycles, jsEntry, cssEntry, removeCss }: MicroAppConfig & {lifecycles?: LifecyclesIF<any>}) {
        console.info(`[DOLLY INFO] Add MicroApp:${appName}`)
        this.loadOneApp({
            rule,
            appId,
            appName,
            getMutatedLifecycles: lifecycles ? () => {
                const sbLifecycles = createSandboxLifecycles({
                    removeCss
                });
                return mutateLifecycles(lifecycles, sbLifecycles);
            } : undefined,
            jsEntry: jsEntry || [],
            cssEntry: cssEntry || [],
        })
    }

    async start() {
        sspaStart();
    }

    // unmount apps
    async unMount() {
        const appNames = getAppNames();
        const successed: string[] = [];
        const failed: {
            appName: string,
            error: string
        }[] = [];
        appNames.forEach((appName: string) => {
            unloadApplication(appName);
            unregisterApplication(appName).then(() => {
                successed.push(appName);
            }).catch((e: any) => {
                failed.push({
                    appName: 'appName',
                    error: JSON.stringify(e)
                });
            });
        })
        console.info(`[DOLLY INFO] App unmount success: ${successed.join(',')}`);
        failed.length && console.info(`[DOLLY INFO] App unmount failed: ${failed.map(i => {
            return `${i.appName} failed (${i.error})`
        }).join(';')}`);
    }

    // prefetch resource
    setPrefetch() {
        window.addEventListener("single-spa:first-mount", () => {
            if (Object.keys(this.appConfigs).length !== 0) {
                // use localstorage to entryConfig backups
                writeConfigToLocalstorage(this.appConfigs, this.coreId);
                try {
                    // prefetch data when needed
                    prefetchResource(this.appConfigs, this.prefetch);
                } catch (e) {
                    console.warn("[DOLLY WARN] prefetch error:" + JSON.stringify(e));
                }
            }
        });
    }

    // support event callbacks (see https://zh-hans.single-spa.js.org/docs/api#events)
    addEventListener(event: EventTypes, fn: any) {
        window.addEventListener(`single-spa:${event}`, (e) => {
            fn.bind(this)(e);
        });
    }

    private onJsLoaded = (js: any) => {};

    // generate app entry config
    async getEntryConfig() {
        this.appConfigs = await getMicroAppConfig({
            env: this.env, 
            coreId: this.coreId, 
            identifyKey: this.identifyKey,
            customerConfig: this.customerConfig,
            configHandler: this.configHandler
        });
    }
}

export { default as addShare } from "./utils/share";
export { loadSingleApp } from "./singleApp/app";
export { getMicroAppConfig } from "./appExplore";
